<script setup lang="ts">
import { computed, inject, ref, useTemplateRef, watchEffect } from 'vue';
import { INSPECTOR_HOST } from '@/composables/inspectorHostContext';
import { RECORD_PANE_NAVIGATOR } from '@/composables/recordPaneTrail';
import { buildEntityColumns, entityDisplayNameFor } from '@/composables/entityColumns';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { useRecordLayout } from '@/composables/recordLayout/useRecordLayout';
import { useAuthStore } from '@/stores/authStore';
import { areaEntity, nounInSentence } from '@/navigation/areas';
import RecordBody from '@/components/record/RecordBody.vue';
import CustomizeRecordDialog from '@/components/record/CustomizeRecordDialog.vue';
import RecordMoreMenu from '@/components/record/RecordMoreMenu.vue';
import { recordHeadingText } from '@/composables/recordLayout/recordHeading';
import { recordNounFor } from '@/composables/recordLayout/useRecordHeading';
import { useInspectorStore } from '@/stores/inspectorStore';

/**
 * The side peek's content: one record by entity and id
 * ({@see useModelRecordQuery}), drawn by RecordBody, the same body as the
 * record's own page, from the same layout. So the peek shows the name, the
 * pinned chips and the sections in the order you chose.
 *
 * Its ⋯ menu, in the peek's header, holds the same actions as the record
 * page's: Open in Admin app, Copy ID and Customize page…. Customize works here
 * for your own layout only. Editing Everyone's default stays on the full page,
 * which asks before you leave with it unsaved.
 */
const props = defineProps<{
    /** models.json key, e.g. "Account", "CrossfadeType". */
    entityId: string;
    recordId: number | string;
    /**
     * Which section the entity's inspector should land on, for callers that are
     * opening a record to show one particular thing — the track card's lyrics
     * chip opens a Live Track on Lyrics rather than on Details.
     *
     * Forwarded blindly. Inspectors that have no sections ignore it, and one
     * that does not offer the named section falls back to its default, so this
     * is a hint rather than a contract.
     */
    initialSection?: string | null;
    /**
     * Leave out the ⋯ menu and its Customize dialog. A list's pane draws its own
     * ⋯ menu for the record it shows, in its header.
     */
    hideMoreMenu?: boolean;
}>();

const columns = computed(() => buildEntityColumns(props.entityId));
const authStore = useAuthStore();
const layoutState = useRecordLayout(() => props.entityId, columns, { onForbidden: () => void authStore.loadUser() });
const customizeOpen = ref(false);
const moreMenu = useTemplateRef<InstanceType<typeof RecordMoreMenu>>('moreMenu');
const headingNoun = computed(() => areaEntity(props.entityId)?.noun ?? entityDisplayNameFor(props.entityId));
const noun = computed(() => nounInSentence(headingNoun.value));

// Inside a peek host the ⋯ menu moves into the host's header. Anywhere else,
// such as in a test, it stays in place.
const hasHeaderSlot = inject(INSPECTOR_HOST, false);

const recordQuery = useModelRecordQuery(
    () => props.entityId,
    () => props.recordId
);

// Once the record has loaded, its peek takes the record's name, the same title
// the body draws, so the peek's trail reads "Main St Café" and not "Location #12".
const inspectorStore = useInspectorStore();
// Inside a list's pane, the pane's trail names this record too.
const paneNavigator = inject(RECORD_PANE_NAVIGATOR, null);
watchEffect(() => {
    const row = recordQuery.data.value;
    if (!row) return;
    const heading = recordHeadingText(layoutState.shown.value, columns.value, row, recordNounFor(props.entityId), props.recordId);
    inspectorStore.renameRecordViews(props.entityId, props.recordId, heading.title);
    paneNavigator?.renameRecord?.(props.entityId, props.recordId, heading.title);
});
</script>
<template>
    <!-- min-h-full flex chain: hosts (InspectorPanel, InspectorSheet, RecordPane) render this inside a
         bounded overflow-auto scrollport. Without the chain, a detail component that wants to
         FILL leftover height (LocationZonePlayerInspector's logs card) sees an auto-height
         parent and grows to its content instead — unbounded logs. -->
    <div data-testid="model-record-inspector" class="flex min-h-full flex-col">
        <p v-if="recordQuery.isLoading.value" class="text-muted-foreground text-sm">Loading…</p>
        <!-- 403 is a routine outcome here since SAU-287: a foreign-key cell
             offers click-to-inspect for any entity in models.json, including
             ones the user has no grant on. Name that first — "it may not
             exist" sent people hunting for a deleted record. -->
        <p v-else-if="recordQuery.isError.value" class="text-muted-foreground text-sm">
            Couldn't load {{ entityId }} #{{ recordId }} — you may not have access to it, or it
            may not exist.
        </p>
        <template v-else-if="recordQuery.data.value">
            <!-- In the peek's header bar, left of the full-page button. `defer` waits for
                 the header to render, since this content can mount first. The same size
                 as the full-page and close buttons beside it. The list's pane draws its
                 own menu, so it passes `hide-more-menu`. -->
            <Teleport v-if="!hideMoreMenu" defer to="#inspector-header-actions" :disabled="!hasHeaderSlot">
                <RecordMoreMenu
                    ref="moreMenu"
                    :entity-id="entityId"
                    :record-id="recordId"
                    :noun="headingNoun"
                    size="compact"
                    test-id-prefix="peek-more"
                    @customize="customizeOpen = true"
                />
            </Teleport>
            <CustomizeRecordDialog
                v-if="!hideMoreMenu"
                v-model:open="customizeOpen"
                :entity-id="entityId"
                :noun="noun"
                :columns="columns"
                :layout-state="layoutState"
                :is-developer="false"
                @closed="moreMenu?.focusTrigger()"
            />
            <RecordBody
                compact
                :entity-id="entityId"
                :record-id="recordId"
                :row="recordQuery.data.value"
                :layout="layoutState.shown.value"
                :initial-section="initialSection"
            />
        </template>
    </div>
</template>
